import { Box, CircularProgress, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { type ReactNode } from "react";

import {
  MAX_SCOUT_CONVERSATION_TITLE_LENGTH,
  useScoutConversationTitleRename,
} from "./useScoutConversationTitleRename";

type ScoutConversationTitleProps = {
  conversationTitle?: string;
  showTitle: boolean;
  conversationId?: string;
  tenantId?: string;
};

const titleTypographySx = {
  fontSize: { xs: "1rem", sm: "1.125rem" },
  fontWeight: 400,
  lineHeight: 1.4,
} as const;

export function ScoutConversationTitle({
  conversationTitle,
  showTitle,
  conversationId,
  tenantId,
}: ScoutConversationTitleProps) {
  const title = conversationTitle?.trim();
  const {
    canRename,
    draftTitle,
    setDraftTitle,
    showSaveSpinner,
    hasUnsavedTitleChange,
    setIsFocused,
    handleBlur,
  } = useScoutConversationTitleRename({
    conversationId,
    tenantId,
    showTitle,
    title,
  });

  let titleContent: ReactNode = null;
  if (showTitle && !title) {
    titleContent = (
      <Skeleton
        variant="text"
        width={220}
        sx={{
          ...titleTypographySx,
        }}
      />
    );
  } else if (showTitle && title && !canRename) {
    titleContent = (
      <Typography
        variant="h6"
        color="text.secondary"
        noWrap
        sx={{
          ...titleTypographySx,
          minWidth: 0,
        }}
      >
        {title}
      </Typography>
    );
  } else if (showTitle && canRename) {
    titleContent = (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minWidth: 0, maxWidth: "100%" }}
      >
        <Box
          sx={{
            display: "inline-grid",
            alignItems: "center",
            maxWidth: "100%",
          }}
        >
          {/* Mirror sizes the grid to the text so the underline tracks content width. */}
          <Box
            component="span"
            aria-hidden
            sx={{
              ...titleTypographySx,
              gridArea: "1 / 1",
              visibility: "hidden",
              whiteSpace: "pre",
              overflow: "hidden",
            }}
          >
            {draftTitle.length > 0 ? draftTitle : " "}
          </Box>
          <TextField
            value={draftTitle}
            onChange={(event) => setDraftTitle(event.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            size="small"
            variant="standard"
            inputProps={{
              // Native inputs default to size=20, which forces ~20ch of width.
              size: 1,
              maxLength: MAX_SCOUT_CONVERSATION_TITLE_LENGTH,
              "aria-label": "Conversation title",
            }}
            sx={{
              gridArea: "1 / 1",
              width: "100%",
              "& .MuiInput-root": {
                mt: 0,
                width: "100%",
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "transparent",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "text.secondary",
                borderBottomWidth: 1,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "primary.main",
              },
              "& .MuiInputBase-input": {
                ...titleTypographySx,
                color: "text.secondary",
                // Drop MUI standard vertical padding so the field height matches
                // the text line and centers with the spinner.
                py: 0,
                height: "auto",
                width: "100%",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
        {showSaveSpinner ? (
          <CircularProgress
            size={14}
            thickness={5}
            aria-label="Saving conversation title"
            sx={{ flexShrink: 0, color: "text.secondary" }}
          />
        ) : null}
      </Stack>
    );
  }

  return (
    <>
      <UnfinishedChangesPrompt
        when={hasUnsavedTitleChange}
        header="Your conversation title hasn't finished saving. Are you sure you want to leave?"
      />
      {titleContent}
    </>
  );
}
